import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {inject, observer} from 'mobx-react';
import {action, observable, reaction, runInAction, toJS} from 'mobx';
import {fabric} from 'fabric';
import 'fabric-history';
import {toast} from 'react-toastify';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import {guid} from '../../../utils/commonUtility';
import CircularLoader from '../../../components/circularLoader/CircularLoader';
import {canvasTemplateUrl} from '../../../constants/constants';
import {ImageTypes, ProjectAssetsType} from '../../../constants/enums';
import {
    defaultColorMarkupPanel,
    defaultHighlighterColor,
    defaultTextColorMarkupPanel,
    defaultThickness,
    green,
    hexToRGB,
    thicknessMarkupPanel,
    white
} from '../../../themes/colors';
import {isAdmin, isGuest, isSchoolAdmin, isStudent, isTeacher} from '../../../utils/userUtility';
import {isDesktop, isMobile} from '../../../utils/screenUtility';
import {ReactComponent as CopySvg} from '../../../assets/images/studio/markup/icon-copy.svg';
import {ReactComponent as PasteSvg} from '../../../assets/images/studio/markup/icon-paste.svg';
import {ReactComponent as PasteDisabledSvg} from '../../../assets/images/studio/markup/icon-paste-disabled.svg';
import {ReactComponent as RotateSvg} from '../../../assets/images/studio/markup/rotate-blue.svg';
import {ReactComponent as MirrorSvg} from '../../../assets/images/studio/markup/icon-mirror.svg';
import {ReactComponent as MirrorVerticalSvg} from '../../../assets/images/studio/markup/icon-mirror-vertical.svg';
import {ReactComponent as DeleteSvg} from '../../../assets/images/studio/markup/icon-delete.svg';
import {diff, patch} from 'jsondiffpatch';
import './styles.scss';
import MarkupPanelContainer from '../markup/MarkupPanelContainer';
import WhiteBoardCursor from "./WhiteBoardCursor";

const HELP_PANEL_WIDTH = 232;
const HELP_PANEL_HEIGHT = 40;
const TEXT_WIDTH = 200;


@inject('projectStore', 'notationViewerStore', 'whiteBoardStore', 'wizardStore', 'iconStore')
@observer
class WhiteBoard extends Component {
    @observable modes = {
        selection: false,
        isDrawingMode: true,
        isHighlighterMode: false,
        isTextMode: false,
        isCircleMode: false,
        isAnnotationMode: false,
        isShapeMode: false,
    };
    @observable canUndo = false;
    @observable canRedo = false;
    @observable canvasWidth = this.props.width;
    @observable canvasHeight = this.props.height;
    @observable scaleFactor = 1;
    @observable color = defaultColorMarkupPanel;
    @observable textBoxDefaultColor = defaultTextColorMarkupPanel;
    @observable thickness = defaultThickness;
    @observable loading = false;
    @observable activeSvg = {
        title: false,
        panel: false,
        canvas: false,
        colored: false,
    };
    @observable editShape = false;
    @observable clipboard = null;
    @observable pasteAmount = 0;
    @observable currentShape = 'rectangle';
    @observable startShapeDrawingPosition = {x: 0, y: 0};
    @observable isDrawingShapeStarted = false;
    @observable isRotated = false;
    @observable activeCursor = null;


    activeStepIndexReaction = null;
    scaleReaction = null;
    lastSendData = {json: {}, patchNum: 0};
    dirty = false;

    historyMap = {};

    uuid = guid();

    componentDidCatch(err, info) {
        console.log(err);
    }

    @action activateMode = (mode) => {
        Object.keys(this.modes).forEach(m => {
            this.modes[m] = m === mode;
            this.canvas[m] = m === mode;
        });

        if (mode === 'isHighlighterMode') {
            this.canvas.isDrawingMode = true;
        }
    };

    @action changeColor = (color) => {
        this.color = color || defaultColorMarkupPanel;
        this.activateColor();
        this.setCursor();
    };

    @action changeThickness = (thickness) => {
        this.thickness = thickness || defaultThickness;
        this.canvas.freeDrawingBrush.width = this.thickness;
    };

    isOwnCanvas = () => {
        return ((isTeacher() || isSchoolAdmin() || isAdmin()) && this.props.isTeacher) || (isStudent() && !this.props.isTeacher)
    }

    sendUpdate = debounce(action(async () => {
        const {id, page, isTeacher, whiteBoardStore, projectStore, wizardStore} = this.props;
        this.dirty = true;
        let currentJson = this.canvas.toJSON();
        const canvasDiff = diff(this.lastSendData.json, currentJson);
        if (this.isOwnCanvas() && canvasDiff) {
            this.lastSendData.patchNum++;
            const step = wizardStore.currentStepType;

            await whiteBoardStore.sendCanvasUpdate(projectStore.activeProjectID, {
                id: id,
                page: page,
                isTeacher: isTeacher,
                patch: canvasDiff,
                isNotation: step === ProjectAssetsType.Notation,
                patchNum: this.lastSendData.patchNum
            });

            this.lastSendData = {
                json: currentJson,
                patchNum: this.lastSendData.patchNum
            };
            await this.saveCanvas(null, null, null, true)
        }

    }), 250, {leading: false, trailing: true, maxWait: 250})

    @action updatePatch = (canvasPatch) => {
        // only allow updated to canvas which are not "ours"
        if (this.isOwnCanvas()) {
            return;
        }
        try {
            let currentJson = this.canvas.toJSON();
            currentJson = patch(currentJson, canvasPatch);
            this.canvas.loadFromJSON(currentJson);
        } catch (err) {
            console.log('error updating canvas', err);
        }
    }


    @action updateHistoryButtons = () => {
        if (this.canvas) {
            this.canUndo = !!this.canvas.historyUndo.length;
            this.canRedo = !!this.canvas.historyRedo.length;
        }
    };

    debounceCanvasDimensions = debounce(this.setDimensions, 250);

    @action setDimensions = () => {
        const {width, height, scale, isNotation} = this.props;

        if (!this.canvas) {
            return;
        }
        if (this.container) {
            this.canvasWidth = this.container.clientWidth || this.canvasWidth;
            this.canvasHeight = this.container.clientHeight || this.canvasHeight;
        }
        if (this.props.isNotation) {
            this.scaleFactor = 1;
            this.canvasWidth = 1024;
            this.canvasHeight = height * (1 / scale) * (1024 / width);
        } else {
            const userRotated = [90, 270].includes(this.props.rotate);
            this.isRotated = this.imgNode?.naturalHeight > this.imgNode?.naturalWidth;
            let realRatio = this.imgNode?.naturalWidth / this.imgNode?.naturalHeight;
            const isRotated = userRotated;
            if (isRotated) {
                realRatio = 1 / realRatio;
            }
            const isLandscape = window.innerWidth > window.innerHeight;

            // this.props.isTeacher && console.log("Before changes: ", this.canvasWidth, this.canvasHeight, realRatio, isRotated, isLandscape);
            if (!isRotated && isLandscape) {
                this.canvasHeight = Math.min(this.canvasWidth / realRatio, this.canvasHeight, window.innerHeight);
                this.canvasWidth = this.canvasHeight * realRatio;
            } else if (isRotated && isLandscape) {
                this.canvasWidth = Math.min(this.canvasHeight * realRatio, this.canvasWidth, window.innerWidth);
                this.canvasHeight = this.canvasWidth / realRatio;
            } else if (isRotated && !isLandscape) {
                this.canvasWidth = Math.min(this.canvasHeight * realRatio, this.canvasWidth, window.innerWidth);
                this.canvasHeight = this.canvasWidth / realRatio;
            } else if (!isRotated && !isLandscape) {
                this.canvasWidth = Math.min(this.canvasHeight * realRatio, this.canvasWidth, window.innerHeight);
                this.canvasHeight = this.canvasWidth / realRatio;
            }
            this.canvasWidth = this.canvasWidth || this.props.width;
            this.canvasHeight = this.canvasHeight || this.props.height;

            this.scaleFactor = this.canvasWidth / width;
            // this.props.isTeacher && console.log('canvas dimension: ', this.canvasWidth, this.canvasHeight, this.props.width, this.props.height, this.scaleFactor, isRotated, realRatio);
        }
        if (this.canvas) {
            try {
                this.canvas.setWidth(this.canvasWidth);
                this.canvas.setHeight(this.canvasHeight * (isNotation ? scale : 1));
            } catch (err) {
                console.log('error setting canvas width');
            }
        }


        if (this.canvas && !isNotation) {
            this.canvas.setZoom(this.scaleFactor);
        }
    };

    @action setLoading = (val) => {
        this.loading = val;
    };

    @action toggleCanvasClick = (isCanvasActive) => {
        this.pasteAmount = 0;
        this.editShape = isCanvasActive;
    };

    dispose = () => {
        if (this.canvas) {
            try {
                this.canvas.setWidth(0);
                this.canvas.setHeight(0);
            } catch (err) {
            }
            try {
                this.canvas.dispose();
            } catch (err) {
            }
        }

    }

    initCanvas = async (isTeacher, canvas) => {
        // console.log('reaction init canvas', isTeacher, canvas?.id)
        const {whiteBoardStore, wizardStore, id, page} = this.props;
        this.dispose();
        const stepType = wizardStore.currentStepType;
        whiteBoardStore.setRef(id, page, isTeacher, stepType === ProjectAssetsType.Notation, this);

        let canvasJson = this.historyMap[whiteBoardStore.getKey(id, page, isTeacher)];

        if (canvas.s3TeacherKey && isTeacher) {
            await this.loadJsonIntoCanvas(canvas.s3TeacherKey, canvasJson);
        } else if (canvas.s3StudentKey && !isTeacher) {
            await this.loadJsonIntoCanvas(canvas.s3StudentKey, canvasJson);
        } else {
            this.setupCanvas();
            this.initializeCanvas();
        }
        // console.log('reaction init canvas done', isTeacher, canvas?.id);
    };

    async componentDidMount() {
        const {id, page, isTeacher, canvas, whiteBoardStore, wizardStore, projectStore} = this.props;
        this.currentCanvas = canvas;
        this.currentId = id;
        this.currentPage = page;
        this.canvasReaction = reaction(() => {
            return {canvas: this.props.canvas, page: this.props.page, id: this.props.id}
        }, async ({canvas, page, id}) => {
            if (this.currentId !== id || this.currentPage !== page) {
                this.currentCanvas = canvas;
                this.currentId = id;
                this.currentPage = page;
                this.initCanvas(isTeacher, canvas);
            }
        })
        this.fullScreenReation = reaction(() => wizardStore.fullScreenMode, () => {
            setTimeout(() => {
                this.debounceCanvasDimensions()
            });
        });
        await this.initCanvas(isTeacher, canvas);
        if (this.currentCanvas?.id !== canvas?.id) {
            console.log('canvas id mismatch, calling dispose');
            await this.initCanvas(isTeacher, this.currentCanvas);
        }


        window.addEventListener('resize', this.debounceCanvasDimensions);
        window.addEventListener('keydown', this.handleRemove);

        // console.log('setting ref did mount', id, isTeacher);
        const isNotation = wizardStore.currentStepType === ProjectAssetsType.Notation;
        whiteBoardStore.setRef(this.currentId, this.currentPage, isTeacher, isNotation, this);

        setTimeout(() => {
            if (this.isOwnCanvas() && projectStore.autoStartCanvas) {
                projectStore.editCanvas(projectStore.autoStartCanvas);
                projectStore.setAutoStartCanvas(false);
            }
        })

        this.setSelection(true);

        setTimeout(() => {
            this.setDimensions();
        })
        this.scaleReaction = reaction(() => this.props.scale, () => {
            this.fixScaleScroll();
        }, {fireImmediately: true});
    }

    fixScaleScroll = () => {
        setTimeout(() => {
            if (this.props.isTeacher && this.modes.isAnnotationMode) {
                this.setCursor();
            }
            if (this.container) {
                const parent = this.container.parentElement.parentElement.parentElement;
                if (!parent) {
                    return;
                }
                let scrollX = 0;
                let scrollY = 0;
                if (isMobile() && !this.props.isNotation) {
                    scrollY = parent.offsetHeight / (2 / (this.props.scale - 1)) - 60;
                }
                if (this.props.isNotation) {
                    scrollX = parent.scrollWidth - (parent.offsetWidth + this.props.width * this.props.scale) / 2;
                } else {
                    scrollX = parent.offsetWidth / (2 / (this.props.scale - 1));
                }
                if (parent) {
                    parent.focus();
                    parent.scrollTo(Math.round(scrollX), Math.round(scrollY));
                }
            }
        })
    };

    async componentDidUpdate(prevProps) {
        const {wizardStore, whiteBoardStore, isTeacher, width, height, scale, canvas, rotate} = this.props;

        if (prevProps.width !== width || prevProps.height !== height || prevProps.scale !== scale || prevProps.rotate !== rotate) {
            setTimeout(this.debounceCanvasDimensions);
        }
        if (prevProps.canvas?.id !== canvas.id) {
            if (!isTeacher && wizardStore.presented && this.lastSendData?.patchNum > 0) {
                let json = this.canvas.toJSON();
                // console.log('storing json in cache');
                this.historyMap[whiteBoardStore.getKey(prevProps.id, prevProps.page, prevProps.isTeacher)] = json;
            }
        }
    }


    componentWillUnmount() {
        if (this.dirty && this.lastSendData.patchNum > 0) {
            try {
                this.saveCanvas();
            } catch (err) {
                console.log('error saving canvas on unmount', err);
            }
        }

        // release all reactions
        this.scaleReaction && this.scaleReaction();
        this.fullScreenReation && this.fullScreenReation();
        this.canvasReaction && this.canvasReaction();

        this.dispose();
        window.removeEventListener('resize', this.debounceCanvasDimensions);
        window.removeEventListener('keydown', this.handleRemove);
    }

    setSelection = (selectionValue) => {
        this.canvas.selection = selectionValue;
        this.canvas.forEachObject((o) => {
            o.selectable = selectionValue;
        });
    }

    handleRemove = (e) => {
        if (e.repeat) return;
        const key = e.which || e.keyCode;
        if (key === 8 || key === 46) {
            this.removeSelectedObjects();
        }
    };

    @action initializeCanvas = () => {
        this.currentState = this.canvas.historyNextState;
        this.lastSendData.json = this.canvas.toJSON();
        this.lastSendData.patchNum = 0;

        this.canvas.on({
            'object:modified': this.updateHistoryButtons,
            'object:added': this.updateHistoryButtons,
            'object:removed': this.updateHistoryButtons,
            'after:render': this.sendUpdate
        });
        this.debounceCanvasDimensions();
    };

    getCanvasId = () => {
        let canvasPrefix = this.props.isTeacher ? 'teacher-canvas' : 'student-canvas';
        return `${this.uuid}_${canvasPrefix}`;
    };

    setupCanvas = () => {
        this.dispose();
        this.canvas = new fabric.Canvas(this.getCanvasId(), {
            width: this.props.width,
            height: this.props.height,
            isDrawingMode: this.modes.isDrawingMode,
            selection: this.modes.selection,
            selectionLineWidth: 1,
            preserveObjectStacking: true,
            selectionBorderColor: '#535878',
            selectionColor: 'rgba(43, 44, 231, 0.3)',
            // controlsAboveOverlay: true,
            // centeredScaling: true,
            centeredRotation: true,
            originX: 'center',
            originY: 'center',
        });

        this.canvas.freeDrawingBrush.width = defaultThickness;
        this.canvas.freeDrawingBrush.color = hexToRGB(defaultColorMarkupPanel);
    };

    loadJsonIntoCanvas = async (key, canvasJson) => {
        const {id, page, isTeacher, isNotation, whiteBoardStore} = this.props;
        return new Promise(async (resolve) => {
            let json;
            if (!canvasJson) {
                const res = await fetch(key);
                json = await res.json();
            } else {
                json = canvasJson;
                // console.log('loading prev json:', json);
            }
            this.setupCanvas();
            this.canvas.loadFromJSON(json, () => {
                this.initializeCanvas();
                this.updateHistoryButtons();
                setTimeout(() => {
                    let patches = whiteBoardStore.getPatches(id, page, isTeacher, isNotation);
                    patches.map(patch => {
                        this.updatePatch(patch.patch);
                    })
                    whiteBoardStore.deletePatches(id, page, isTeacher, isNotation);
                });
                resolve();
            });
        })

    };

    activateColor = () => {
        if (this.modes.isHighlighterMode) {
            this.canvas.freeDrawingBrush.color = hexToRGB(this.color, '0.5');
        } else {
            this.canvas.freeDrawingBrush.color = this.color;
        }
    };

    undo = () => {
        this.canvas.undo();
        this.updateHistoryButtons();
    };

    redo = () => {
        this.canvas.redo();
        this.updateHistoryButtons();
    };

    clearAll = () => {
        this.canvas.clear();
        this.updateHistoryButtons();
    };

    reset = () => {
        this.changeColor();
        this.changeThickness();
        this.dirty = false;
    };

    removeSelectedObjects = () => {
        const activeObjects = this.canvas.getActiveObjects();
        const activeObject = this.canvas.getActiveObject();
        if (activeObjects.length && !activeObject.isEditing) {
            this.canvas.discardActiveObject();
            this.canvas.remove(...activeObjects);

            this.toggleCanvasClick(false);
        }
    };

    clickAway = () => {
        this.toggleCanvasClick(false);
        if (this.canvas && this.canvas.getActiveObject()) {
            this.canvas.discardActiveObject();
            this.canvas.renderAll();
            this.resetCursor();
        }
    };

    removeEvents = () => {
        this.canvas.off('mouse:down');
        this.canvas.off('mouse:up');
        this.canvas.off('mouse:move');
        this.canvas.off('selection:updated');
        this.canvas.off('selection:created');
    };

    fixDrawingPosition = () => {
        if (this.isOwnCanvas()) {
            this.canvas.on('object:added', (options) => {
                this.selectedTarget = options.target;

                if (options.target.originX !== 'center') {
                    options.target.set({
                        originX: 'center',
                        originY: 'center',
                        left: this.selectedTarget.left + (this.selectedTarget.width / 2) + (this.selectedTarget.strokeWidth / 2),
                        top: this.selectedTarget.top + (this.selectedTarget.height / 2) + (this.selectedTarget.strokeWidth / 2),
                    });
                }
            });
        }
    }

    enableDrawingMode = () => {
        this.removeEvents();
        this.activateMode('isDrawingMode');
        this.canvas.freeDrawingBrush.color = this.color;
        this.canvas.freeDrawingBrush.width = this.thickness;
        this.fixDrawingPosition();
    };

    enableHighlighterMode = () => {
        this.removeEvents();
        this.activateMode('isHighlighterMode');
        this.canvas.freeDrawingBrush.color = hexToRGB(defaultHighlighterColor, '0.5');
        this.canvas.freeDrawingBrush.width = thicknessMarkupPanel[3];
        this.fixDrawingPosition();
    };

    enableTextMode = () => {
        this.removeEvents();
        this.activateMode('isTextMode');
        this.addTextEvents();
    };

    enableAnnotationMode = () => {
        this.removeEvents();
        this.activateMode('isAnnotationMode');
        this.addAnnotationEvents();
    };

    enableSelectionMode = () => {
        this.removeEvents();
        this.activateMode('selection');
        this.setSelection(true);
        this.addSelectionEvents();
        this.resetCursor();
    };


    addCanvasFile = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (f) => {
            const data = f.target.result;
            fabric.Image.fromURL(data, (img) => {
                const widthAspectRatio = this.props.width / img.width;
                const heightAspectRatio = this.props.height / img.height;
                const finalAspectRatio = Math.min(widthAspectRatio, heightAspectRatio);
                const oImg = img.set({
                    left: 0,
                    top: 0,
                    angle: 0
                });
                if (finalAspectRatio < 1) {
                    oImg.scale(finalAspectRatio);
                }
                oImg.toObject = (function (toObject) {
                    return function () {
                        return fabric.util.object.extend(toObject.call(this), {
                            blob: this.blob
                        });
                    };
                })(oImg.toObject);
                this.canvas.add(oImg).setActiveObject(oImg);
                this.enableSelectionMode();
                oImg.blob = file;
            }, {crossOrigin: 'anonymous'});
        };
        reader.readAsDataURL(file);
    };

    addAnnotationEvents = () => {
        this.canvas.on('mouse:down', (options) => {
            this.selectedTarget = options.target;

            if (options.target === null && this.canvas.isAnnotationMode) {
                this.toggleCanvasClick(false);
                this.addSvg(options.pointer);
            }
            if (options.target) {
                this.toggleCanvasClick(true);
            }
        });

        this.canvas.on('object:moved', (options) => {
            this.toggleCanvasClick(false);
            this.selectedTarget = options.target;

            if (options.target) {
                this.toggleCanvasClick(true);
            }
        });

        this.canvas.on('selection:created', (options) => {
            this.toggleCanvasClick(false);
            this.selectedTarget = options.target;

            if (options.target) {
                this.toggleCanvasClick(true);
            }
        });

        this.canvas.on('selection:updated', (options) => {
            this.toggleCanvasClick(false);
            this.selectedTarget = options.target;

            if (options.target) {
                this.toggleCanvasClick(true);
            }
        });
    };

    goToSelectionMode = () => {
        this.activateMode('selection');
        this.toggleCanvasClick(true);
    }

    addSelectionEvents = () => {
        this.canvas.on('mouse:down', (options) => {
            this.toggleCanvasClick(false);
            this.selectedTarget = options.target;
            const allObjects = this.canvas.getObjects();
            const emptyTextObjects = allObjects.filter(el => el.text === '');

            this.canvas.remove(...emptyTextObjects);

            if (options.target) {
                this.goToSelectionMode();
            }
        });

        this.canvas.on('object:moved', (options) => {
            this.toggleCanvasClick(false);
            this.selectedTarget = options.target;
        });

        this.canvas.on('selection:created', (options) => {
            this.toggleCanvasClick(false);
            this.selectedTarget = options.target;

            if (options.target) {
                this.goToSelectionMode();
            }
        });

        this.canvas.on('selection:updated', (options) => {
            this.toggleCanvasClick(false);
            this.selectedTarget = options.target;

            if (options.target) {
                this.goToSelectionMode();
            }
        });
    };

    @action setCursor = async (icon) => {
        if (!this.canvas) return;
        if (icon) {
            this.activeIcon = icon;
        }
        if (!this.activeIcon) return;

        const encodedSvg = await this.activeIcon.getCanvasSvgWithColorAndScale(this.color, 1);
        runInAction(() => {
            this.activeCursor = `data:image/svg+xml;base64,${encodedSvg}`;
        })
        this.canvas.set({defaultCursor: 'none'});
    }

    @action resetCursor = () => {
        if (!this.canvas) return;
        this.activeCursor = null;
        this.activeIcon = null;
        this.canvas.set({defaultCursor: 'default'});
    }

    addSvg = async (pointer) => {
        if (!this.activeIcon?.key || !pointer) return;
        const svg = await this.activeIcon.getCanvasSvgWithColorAndScaleTxt();
        fabric.loadSVGFromString(svg, (objects, options) => {
            const svg = fabric.util.groupSVGElements(objects, options);
            svg.set({
                left: (pointer.x / this.scaleFactor),
                top: (pointer.y / this.scaleFactor),
                fill: this.color,
                originX: 'center',
                originY: 'center',
                objectCaching: false
            });
            if (svg._objects && !this.activeIcon?.disableModifying) {
                svg._objects.forEach(object => object.fill = this.color)
            }
            this.canvas.add(svg);
        });
    };

    addTextEvents = () => {
        this.canvas.on('mouse:down', (options) => {
            if (options.target === null && this.canvas.isTextMode) {
                const allObjects = this.canvas.getObjects();
                const emptyTextObjects = allObjects.filter(el => el.text === '');

                this.canvas.remove(...emptyTextObjects);
                this.addText(options.pointer);
            }
            this.selectedTarget = options.target;
        });
    };

    addText = (pointer) => {
        const textBox = new fabric.Textbox('', {
            width: TEXT_WIDTH,
            top: pointer.y / this.scaleFactor,
            left: pointer.x / this.scaleFactor,
            originX: 'left',
            originY: 'center',
            fontSize: 16,
            textAlign: 'left',
            fontWeight: 400,
            fontFamily: 'Poppins',
            fill: hexToRGB(this.color),
            padding: 5,
            hoverCursor: 'text',
            borderColor: `rgb(${this.textBoxDefaultColor})`,
            cornerColor: `rgb(${this.textBoxDefaultColor})`,
            cornerSize: 6,
            transparentCorners: true,
            cornerStyle: 'circle',
        });

        this.canvas.add(textBox);

        this.canvas.setActiveObject(textBox);
        textBox.enterEditing();
        textBox.hiddenTextarea.focus();
    };

    @action addShape = (type) => {
        this.currentShape = type;
    };

    enableShapeMode = () => {
        this.removeEvents();
        this.activateMode('isShapeMode');
        this.addShapeEvents();
        this.canvas.set({defaultCursor: 'crosshair'});
    };

    addShapeEvents = () => {
        this.canvas.on('mouse:down', (options) => {
            if (options.target === null) {
                this.startShapeDrawingPosition.x = options.pointer.x;
                this.startShapeDrawingPosition.y = options.pointer.y;
                this.addFigure(options.pointer);
                this.isDrawingShapeStarted = this.canvas.getActiveObject() !== null;
            }
        });

        this.canvas.on('mouse:move', (options) => {
            if (this.isDrawingShapeStarted) {
                const pointer = this.canvas.getPointer(options.e);
                this.onMouseDrawShape(pointer.x, pointer.y);
                this.canvas.renderAll();
            }
        });

        this.canvas.on('mouse:up', () => {
            const allObjects = this.canvas.getObjects();
            const emptyTextObjects = allObjects.filter(el => el.width === 0 && el.height === 0);

            this.canvas.remove(...emptyTextObjects);
            this.isDrawingShapeStarted = false;
            this.canvas.renderAll();
        });
    };

    onMouseDrawShape = (x, y) => {
        const shape = this.canvas.getActiveObject();

        shape && shape.set({
            originX: this.startShapeDrawingPosition.x / this.scaleFactor > x ? 'right' : 'left',
            originY: this.startShapeDrawingPosition.y / this.scaleFactor > y ? 'bottom' : 'top',
            [this.currentShape === 'rectangle' && 'width']: Math.abs(this.startShapeDrawingPosition.x / this.scaleFactor - x),
            [this.currentShape === 'rectangle' && 'height']: Math.abs(this.startShapeDrawingPosition.y / this.scaleFactor - y),
            [this.currentShape === 'oval' && 'radius']: (Math.sqrt((Math.abs(this.startShapeDrawingPosition.x / this.scaleFactor - x) ** 2) + (Math.abs(this.startShapeDrawingPosition.y / this.scaleFactor - y) ** 2))) / 2,
            [this.currentShape === 'line' && 'x2']: x,
            [this.currentShape === 'line' && 'y2']: y,
        }).setCoords();
    }

    addFigure = (pointer) => {
        let shape;
        switch (this.currentShape) {
            case 'rectangle':
                shape = new fabric.Rect({
                    top: pointer.y / this.scaleFactor,
                    left: pointer.x / this.scaleFactor,
                    strokeWidth: 2,
                    stroke: hexToRGB(this.color),
                    fill: '',
                });

                break;
            case 'oval':
                shape = new fabric.Circle({
                    top: pointer.y / this.scaleFactor,
                    left: pointer.x / this.scaleFactor,
                    radius: 0,
                    strokeWidth: 2,
                    stroke: hexToRGB(this.color),
                    fill: '',
                });

                break;
            case 'line':
                shape = new fabric.Line([pointer.x / this.scaleFactor, pointer.y / this.scaleFactor, pointer.x / this.scaleFactor, pointer.y / this.scaleFactor], {
                    stroke: hexToRGB(this.color),
                    strokeWidth: 2,
                });

                break;
        }

        this.canvas.add(shape);
        this.canvas.setActiveObject(shape);
        this.canvas.renderAll();
    };

    saveCanvas = async (id, canvas, page, skipThumbnail = false) => {
        if (!this.isOwnCanvas()) {
            return;
        }
        this.currentState = this.canvas.historyNextState;
        const {projectStore, isNotation} = this.props;
        if (isGuest()) {
            if (isNotation) {
                projectStore.editNotationCanvas(false)
            } else {
                projectStore.editCanvas(false);
            }
        }
        if (!id) {
            id = this.props.id;
            canvas = this.props.canvas;
            page = this.props.page;
        }
        if (!canvas.type) {
            await projectStore.addCanvasToNotation(id, null, this.canvas.toJSON(), this.canvasWidth, this.canvasHeight, page, false);
            return;
        }

        if (!skipThumbnail) {
            fabric.Image.fromURL(canvasTemplateUrl(canvas.type), (oImg = {}) => {
                try {
                    if (oImg._element) {
                        this.canvas.setBackgroundImage(oImg, this.canvas.renderAll.bind(this.canvas), {
                            scaleX: this.canvasWidth / oImg.width / this.scaleFactor,
                            scaleY: this.canvasHeight / oImg.height / this.scaleFactor
                        });
                    }
                    this.canvas.getElement().toBlob(async (blob) => {
                        this.canvas.backgroundImage = null;
                        this.canvas.renderAll();
                        await projectStore.addCanvasToNotation(id, blob, this.canvas.toJSON(), this.canvasWidth, this.canvasHeight, page, false);
                    });
                } catch (err) {
                    console.log(err);
                }
            }, {crossOrigin: 'anonymous'});
        } else {
            await projectStore.addCanvasToNotation(id, null, this.canvas.toJSON(), this.canvasWidth, this.canvasHeight, page, false);
        }
    };

    setContainerRef = (node) => {
        this.container = node;
    };

    close = async () => {
        this.resetCursor();
        await this.saveCanvas();
        const {projectStore} = this.props;
        projectStore.editNotationCanvas(false)
        projectStore.editCanvas(false);
        this.reset();
    };

    imgRef = (node) => {
        this.imgNode = node;
    }

    renderImageTemplate = () => {
        const isRotated = [90, 270].includes(this.props.rotate);
        const width = isRotated ? this.canvasHeight : this.canvasWidth;
        const height = isRotated ? this.canvasWidth : this.canvasHeight;
        let styles = {
            transform: `rotate(${this.props.rotate || 0}deg)`
        }
        styles = {
            ...styles,
            minWidth: width,
            minHeight: height,
            maxWidth: width,
            maxHeight: height,
            width: width,
            height: height
        }
        if (!this.props.canvas.type && this.props.imageUrl) {
            return (
                <img
                    onLoad={this.debounceCanvasDimensions}
                    ref={this.imgRef}
                    alt=""
                    crossOrigin="anonymous"
                    src={this.props.imageUrl}
                    style={styles}
                />
            );
        }
        if (this.props.canvas.type && this.props.canvas.type !== ImageTypes.Blank) {
            return (
                <img
                    onLoad={this.debounceCanvasDimensions}
                    ref={this.imgRef}
                    alt=""
                    crossOrigin="anonymous"
                    src={canvasTemplateUrl(this.props.canvas.type)}
                    style={styles}
                />
            );
        }
        return null;
    };

    @action addCanvasImage = (activeSvg) => {
        this.enableAnnotationMode();
        this.activeSvg = activeSvg;
    };

    getActiveCanvasObjects = () => {
        return this.canvas.getActiveObjects();
    }

    @action copyCanvas = () => {
        const activeCanvas = this.getActiveCanvasObjects();

        this.clipboard = activeCanvas.map(el => fabric.util.object.clone(el));
        toast('Copied to clipboard', {autoClose: 3000, className: {background: green, color: white}});
    }

    pasteCanvas = () => {
        const clipboardArray = toJS(this.clipboard);
        const activeObject = this.canvas.getActiveObject();
        const activeObjects = this.canvas.getActiveObjects();

        clipboardArray.forEach((el) => {
            el.clone((cloned) => {
                const {group, left, top, width} = el;

                let leftPosition = 0;
                let topPosition = 0;

                if (group) {
                    leftPosition = activeObjects.length > 1
                        ? group.left + (group.width / 2) + left + ((group.width + 20) * (this.pasteAmount + 1))
                        : activeObject.left + left + ((group.width + (group.width / 2) + 20) * (this.pasteAmount + 1))
                    topPosition = activeObjects.length > 1
                        ? group.top + (group.height / 2) + top
                        : activeObject.top + (activeObject.height / 2) + top
                } else {
                    leftPosition = activeObjects.length > 1
                        ? activeObjects[0].group.left + activeObjects[0].group.width + ((width + 10) * (this.pasteAmount + 1))
                        : activeObject.left + ((activeObject.width + 10) * (this.pasteAmount + 1))
                    topPosition = activeObjects.length > 1
                        ? activeObjects[0].group.top
                        : activeObject.top
                }

                cloned.set({
                    top: topPosition,
                    left: leftPosition,
                    group: null,
                    originX: 'center',
                    originY: 'center',
                    evented: true
                });
                if (cloned.type === 'activeSelection') {
                    cloned.canvas = this.canvas;
                    cloned.forEachObject((obj) => {
                        this.canvas.add(obj);
                    });
                    cloned.setCoords();
                } else {
                    this.canvas.add(cloned);
                }
            });
        });

        this.pasteAmount += 1;
        this.resetCursor();
        this.canvas.requestRenderAll();
    }

    rotateCanvas = () => {
        const activeCanvas = this.getActiveCanvasObjects();

        activeCanvas.forEach(el => el.set('originX', 'center').set('originY', 'center').set('angle', el.angle + 90));
        this.canvas.renderAll();
    }

    flipCanvas = (isHorizontal) => {
        const activeCanvas = this.getActiveCanvasObjects();

        activeCanvas.forEach(el => el.set(isHorizontal ? 'flipX' : 'flipY', isHorizontal ? !el.flipX : !el.flipY));
        this.canvas.renderAll();
    }

    flipCanvasHorizontal = () => {
        this.flipCanvas(true);
    }

    flipCanvasVertical = () => {
        this.flipCanvas(false);
    }

    canvasEditingPanel = () => {
        const activeObject = this.canvas.getActiveObject();
        const activeObjects = this.canvas.getActiveObjects();
        const positioningContainer = document.getElementsByClassName('canvas-container')[0];

        const parentLeft = positioningContainer.offsetLeft;
        const parentRight = parentLeft + this.canvas.width;
        const parentTop = positioningContainer.offsetTop;
        const parentBottom = parentTop + positioningContainer.offsetHeight;

        let panelTop, panelLeft;
        if (isMobile()) {
            panelTop = parentTop + this.canvas.height;
            panelLeft = parentLeft + ((this.canvas.width / 2) - (HELP_PANEL_WIDTH / 2));
        } else if (activeObject) {
            let activeLeft = activeObject.left;
            let activeTop = activeObject.top;
            let activeWidth = activeObject.width / 2;
            let activeHeight = activeObject.height / 2;

            if (activeObjects.length > 1) {
                activeLeft = activeObjects[0].group.left;
                activeTop = activeObjects[0].group.top;
                activeWidth = activeObjects[0].group.width;
                activeHeight = activeObjects[0].group.height;
            }
            let activeRight = activeLeft + activeWidth;
            let activeBottom = activeTop + activeHeight;

            panelLeft = parentLeft + activeRight * this.scaleFactor + 10;
            panelTop = activeBottom * this.scaleFactor + 20;

            if (panelLeft + HELP_PANEL_WIDTH + 10 > parentRight) {
                panelLeft -= HELP_PANEL_WIDTH;
            }

            if (panelTop + HELP_PANEL_HEIGHT > parentBottom) {
                panelTop = activeTop * this.scaleFactor - 2 * HELP_PANEL_HEIGHT;
            }
        }


        return (
            <div className="canvas-editing-panel" style={{left: panelLeft, top: panelTop}}>
                <button className="canvas-editing-panel__button" onClick={this.copyCanvas}>
                    <CopySvg title='Copy'/>
                </button>
                <button disabled={!this.clipboard} className="canvas-editing-panel__button" onClick={this.pasteCanvas}>
                    {
                        this.clipboard
                            ? <PasteSvg title='Paste'/>
                            : <PasteDisabledSvg title='Paste Disabled'/>
                    }
                </button>
                <button className="canvas-editing-panel__button" onClick={this.rotateCanvas}>
                    <RotateSvg title='Rotate'/>
                </button>
                <button className="canvas-editing-panel__button" onClick={this.flipCanvasHorizontal}>
                    <MirrorSvg title='Flip horizontal'/>
                </button>
                <button className="canvas-editing-panel__button" onClick={this.flipCanvasVertical}>
                    <MirrorVerticalSvg title='Flip vertical'/>
                </button>
                <button className="canvas-editing-panel__button" onClick={this.removeSelectedObjects}>
                    <DeleteSvg title='Delete'/>
                </button>
            </div>
        );
    };

    render() {
        const containerClasses = classNames({
            'white-board': true,
            'student-canvas': !this.props.isTeacher,
            'canvas-loading': this.loading,
            'markup-is-dragging': this.props.whiteBoardStore.isMarkupDragging,
            'is-editable': this.props.isCanvasEditable,
            [this.props.canvas.type]: this.props.canvas.type
        });
        const imgClasses = classNames({
            'image-container': true,
            'not-visible': !this.props.isTeacher,
        });

        let styleProp = {};
        if (this.props.isNotation) {
            const width = 1024;
            const widthScale = (this.props.width || 1024) / width;
            const scale = widthScale * this.props.scale;
            let fixedScale = (width - width * (1 / widthScale)) / 2 / this.props.scale;

            const areWeOffsettingParent = this.container?.parentElement?.offsetWidth < this.canvasWidth * scale;
            if (areWeOffsettingParent) {
                fixedScale = 0;
            }
            styleProp = {
                width: this.canvasWidth,
                height: this.canvasHeight * this.props.scale,
                transform: `scale(${scale}) translate(${fixedScale}px, 0)`,
                transformOrigin: areWeOffsettingParent ? '0 0' : '',
            };
        } else {
            const {scale} = this.props;

            styleProp = {
                transform: `scale(${scale})`,
                transformOrigin: scale > 1 ? '0 0' : '',
            };
        }

        return (
            <div className={containerClasses}>
                {this.loading && <CircularLoader/>}
                {isDesktop() &&
                <WhiteBoardCursor cursor={this.activeCursor} scale={this.props.scale * this.scaleFactor} container={this.container}/>}
                <ClickAwayListener onClickAway={this.clickAway}>
                    <div className="canvas-area" ref={this.setContainerRef} style={styleProp}
                         data-testid={this.props.isTeacher ? 'teacher-canvas' : 'student-canvas'}>
                        <div className={imgClasses}>
                            {this.renderImageTemplate()}
                        </div>
                        <Fragment>
                            <canvas id={this.getCanvasId()}/>
                        </Fragment>
                        {this.editShape && this.canvasEditingPanel()}
                    </div>
                </ClickAwayListener>

                {this.isOwnCanvas() &&
                <div className="notation-markup-container">
                    <MarkupPanelContainer
                        enableSelection={this.enableSelectionMode}
                        isSelectionMode={this.modes.selection}
                        enablePencil={this.enableDrawingMode}
                        isPencilMode={this.modes.isDrawingMode}
                        enableHighlighter={this.enableHighlighterMode}
                        isHighlighterMode={this.modes.isHighlighterMode}
                        enableText={this.enableTextMode}
                        isTextMode={this.modes.isTextMode}
                        changeColor={this.changeColor}
                        changeThickness={this.changeThickness}
                        addCanvasFile={this.addCanvasFile}
                        setCursor={this.setCursor}
                        resetCursor={this.resetCursor}
                        enableAnnotation={this.enableAnnotationMode}
                        isAnnotationMode={this.modes.isAnnotationMode}
                        addCanvasImage={this.addCanvasImage}
                        addShape={this.addShape}
                        enableShape={this.enableShapeMode}
                        isShapeMode={this.modes.isShapeMode}
                        activeShape={this.currentShape}
                        canUndo={this.canUndo}
                        canRedo={this.canRedo}
                        undo={this.undo}
                        redo={this.redo}
                        save={this.saveCanvas}
                        close={this.close}
                        clear={this.clearAll}
                    />
                </div>
                }
            </div>
        );
    }
}

WhiteBoard.propTypes = {
    canvas: PropTypes.object.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    imageUrl: PropTypes.string,
    isNotation: PropTypes.bool,
    isTeacher: PropTypes.bool,
    saveCanvas: PropTypes.func,
    cancelCanvas: PropTypes.func,
};

export default WhiteBoard;
